import { isVehicleCamera, isVehicleWithOverSpeedingAlarm } from '@cobliteam/api-lib';
import type { Vehicle } from '@cobliteam/common-types';
import { useGlobalFilterStore, useVehicle } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Popover, Typography } from '@cobliteam/ui/components';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Sort from '@cobliteam/ui/components/Sort';
import { Checkbox, Label } from '@cobliteam/ui/form';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useUpdateEffect } from 'ahooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DropdownVehiclesPlaceholder from './components/DropdownVehiclesPlaceholder';
import VehicleItem from './components/VehicleItem/VehicleItem';

export enum VehicleOption {
  BUZZER = 'BUZZER',
}

export enum SortOption {
  ALPHABETICAL = 'ALPHABETICAL',
  ASSOCIATED = 'ASSOCIATED',
  DISSOCIATED = 'DISSOCIATED',
}

export interface MultiSelectVehiclesProps {
  size?: 'sm' | 'md';
  title?: string;
  selectedVehicles: Vehicle[];
  isOpen?: boolean;
  isLoading?: boolean;
  vehicleOption?: VehicleOption;
  disabledTooltip?: React.ReactNode | string;
  disabled?: boolean;
  showAudioAlarmPopover?: boolean;
  showToggleAll?: boolean;
  portal?: boolean;
  disabledRule?: (vehicle: Vehicle) => boolean;
  filterRule?: (vehicle: Vehicle) => boolean;
  onChange: (vehicles: Vehicle[]) => void;
  onClear?: () => void;
}

const MultiSelectVehicles = ({
  size = 'sm',
  title,
  isOpen,
  selectedVehicles,
  isLoading,
  vehicleOption,
  disabledTooltip,
  disabled,
  portal = false,
  disabledRule,
  showAudioAlarmPopover,
  showToggleAll,
  filterRule,
  onChange,
  onClear,
}: MultiSelectVehiclesProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedVehicles || []);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [vehicleOrdered, setVehicleOrdered] = useState<Vehicle[] | undefined>(undefined);
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.ALPHABETICAL);
  const [openPopoverConfirmation, setOpenPopoverConfirmation] = useState(false);
  const vehicleGroupIds = useGlobalFilterStore((state) => state.vehicleGroupIds);

  const { data, isFetching } = useVehicle().getVehicles({}, vehicleGroupIds);

  const vehicles = useMemo(() => {
    if (filterRule) return data?.filter(filterRule);
    return data;
  }, [data, filterRule]);

  const onClick = useCallback(() => {
    setDraft(selectedVehicles || []);
  }, [selectedVehicles]);

  const handleClose = useCallback(() => {
    setOpenPopoverConfirmation(false);
    setForceClosed(true);
    setDraft(selectedVehicles || []);
    setSearch(undefined);
  }, [selectedVehicles]);

  const handleClear = useCallback(() => {
    onClear?.();
    setDraft([]);
  }, [onClear]);

  const sortBySelected = useCallback(
    (a: Vehicle, b: Vehicle) => {
      const aIsSelected = selectedVehicles?.some((v) => v.id === a.id);
      const bIsSelected = selectedVehicles?.some((v) => v.id === b.id);
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      return 0;
    },
    [selectedVehicles]
  );

  const sortByLabel = useCallback((a: Vehicle, b: Vehicle) => {
    const aLabel = a.label.toLowerCase() || '';
    const bLabel = b.label.toLowerCase() || '';
    if (aLabel < bLabel) return -1;
    if (aLabel > bLabel) return 1;
    return 0;
  }, []);

  const sortByDeviceAssociation = useCallback(
    (option: SortOption) => (a: Vehicle, b: Vehicle) => {
      const aIsAssociated = !!a.deviceId;
      const bIsAssociated = !!b.deviceId;
      const isAssociated = option === SortOption.ASSOCIATED;
      if (isAssociated) return Number(bIsAssociated) - Number(aIsAssociated);
      return Number(aIsAssociated) - Number(bIsAssociated);
    },
    []
  );

  const getSortMethod = useCallback(
    (option: SortOption) => {
      const associationOptions = [SortOption.ASSOCIATED, SortOption.DISSOCIATED];
      if (associationOptions.includes(option)) {
        return sortByDeviceAssociation(option);
      }

      return sortByLabel;
    },
    [sortByLabel, sortByDeviceAssociation]
  );

  const filterOptions = (vehicle: Vehicle) => {
    if (vehicleOption === VehicleOption.BUZZER) {
      return vehicle.deviceId !== undefined && (isVehicleCamera(vehicle) || isVehicleWithOverSpeedingAlarm(vehicle));
    }
    return true;
  };

  const filterBySearch = useCallback(
    (vehicle: Vehicle) => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return vehicle.label.toLowerCase().includes(searchLower) || (vehicle.alias?.toLowerCase().includes(searchLower) ?? false);
    },
    [search]
  );

  const hasSameElements = selectedVehicles?.length === draft?.length && Array.from(selectedVehicles).every((value) => draft?.some((draftItem) => draftItem.id === value.id));

  const dataFiltered = vehicleOrdered?.filter(filterOptions)?.filter(filterBySearch).sort(sortBySelected);
  const allChecked = draft.length === dataFiltered?.length;
  const noneChecked = draft.length === 0;
  const allCheckBoxValues = useMemo(() => {
    if (allChecked) return true;
    if (noneChecked) return false;
    return 'indeterminate';
  }, [allChecked, noneChecked]);

  const applyFilter = useCallback(() => {
    handleClose();
    onChange(draft);
    if (vehicles) {
      const sortMethod = getSortMethod(sortOption);
      setVehicleOrdered(vehicles.sort(sortMethod).sort(sortBySelected));
    }
  }, [draft, getSortMethod, handleClose, onChange, sortBySelected, sortOption, vehicles]);

  const handleApply = useCallback(() => {
    if (noneChecked && showAudioAlarmPopover) {
      setOpenPopoverConfirmation(true);
      return;
    }

    applyFilter();
  }, [noneChecked, showAudioAlarmPopover, applyFilter]);

  const handleSortChange = (option: SortOption) => {
    if (vehicles) {
      setSortOption(option);
      const sortMethod = getSortMethod(option);
      setVehicleOrdered(vehicles.sort(sortMethod).sort(sortBySelected));
    }
  };

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  useEffect(() => {
    if (vehicles) {
      setVehicleOrdered(vehicles.sort(sortByLabel).sort(sortBySelected));
    }
  }, [vehicles, sortByLabel, sortBySelected]);

  const prevVehicleGroupIds = useRef(vehicleGroupIds);

  useUpdateEffect(() => {
    if (Array.isArray(vehicleGroupIds) && vehicleGroupIds.length > 0 && vehicleGroupIds !== prevVehicleGroupIds.current) {
      setDraft([]);
      onChange([]);
    }
    prevVehicleGroupIds.current = vehicleGroupIds;
  }, [vehicleGroupIds]);

  const sortOptions = [
    { value: SortOption.ALPHABETICAL, key: t('vehicles.sort_alphabetical') },
    { value: SortOption.ASSOCIATED, key: t('vehicles.sort_by_associated') },
    { value: SortOption.DISSOCIATED, key: t('vehicles.sort_by_disassociated') },
  ];

  const toggleAll = useCallback(
    (checked: boolean) => {
      const newDraft = checked && dataFiltered ? dataFiltered : [];
      setDraft(newDraft);
    },
    [dataFiltered]
  );

  const toggleItem = useCallback(
    (dataItem: Vehicle) => {
      const newItemDraft = Array.from(draft);
      const isItemSelected = newItemDraft.some((draftItem) => draftItem.id === dataItem.id);
      if (isItemSelected) newItemDraft.splice(newItemDraft.map((v) => v.id).indexOf(dataItem.id), 1);
      if (!isItemSelected) newItemDraft.push(dataItem);
      setDraft(newItemDraft);
    },
    [draft]
  );

  const countLabel = useMemo(() => {
    const selectedCount = selectedVehicles?.length || 0;
    const allVehiclesSelected = selectedVehicles?.length === vehicles?.length;
    const noneSelected = selectedCount === 0;

    if (showAudioAlarmPopover && allVehiclesSelected) return t('vehicles.all');
    if (showAudioAlarmPopover && noneSelected) return t('vehicles.none');

    return selectedCount > 0 ? `(${selectedCount})` : '';
  }, [selectedVehicles, vehicles, showAudioAlarmPopover, t]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      portal={portal}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='flex items-center justify-between px-2'>
            <Button type='button' variant='ghost' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('vehicles.clear_selection')}
            </Button>
            <Sort
              onChange={(option) => handleSortChange(option.value)}
              options={sortOptions}
              placement='right-start'
              defaultOption={sortOptions.find((option) => sortOption === option.value)}
            />
          </Dropdown.Header>
          <Dropdown.Search className='px-2'>
            <InputSearch placeholder={t('vehicles.search_placeholder')} value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='px-2'>
            {showToggleAll && (
              <div className={cn('mb-2 flex items-center gap-2', { hidden: isLoading || isFetching })}>
                <Checkbox id='toggle-all' checked={allCheckBoxValues} onCheckedChange={toggleAll} />
                <Label htmlFor='toggle-all'>{t('vehicles.select_all')}</Label>
              </div>
            )}
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={dataFiltered || []}
              isLoading={isLoading || isFetching}
              loadingComponent={<Dropdown.Loading />}
              placeholderComponent={<DropdownVehiclesPlaceholder />}
              className={cn({ 'ml-3': showToggleAll })}
              itemComponent={(item: Vehicle) => {
                return (
                  <VehicleItem
                    data={item}
                    onChange={() => toggleItem(item)}
                    checked={draft.some((draftItem) => draftItem.id === item.id)}
                    disabled={disabledRule?.(item)}
                    disabledTooltip={disabledTooltip}
                    grouped
                  />
                );
              }}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button type='button' variant='ghost' full size='sm' onClick={handleClose}>
                {t('vehicles.cancel')}
              </Button>
              <Popover open={openPopoverConfirmation && showAudioAlarmPopover} onOpenChange={(open) => !open && setOpenPopoverConfirmation(open)}>
                <Popover.Trigger asChild>
                  <Button type='button' size='sm' full onClick={handleApply} disabled={hasSameElements}>
                    {t('vehicles.apply')}
                  </Button>
                </Popover.Trigger>
                <Popover.Content align='center' side='left' className='flex w-72 flex-col gap-3 text-black' portal={false}>
                  <div className='flex items-center'>
                    <Typography variant='description-title'>{t('vehicles.apply_changes_disable_alerts')}</Typography>
                  </div>
                  <Typography variant='description-list-default'>{t('vehicles.no_vehicles_selected_description')}</Typography>
                  <div className='flex items-center justify-end gap-2'>
                    <Button variant='ghost' size='sm' onClick={() => setOpenPopoverConfirmation(false)}>
                      {t('vehicles.cancel')}
                    </Button>
                    <Button color='primary' size='sm' onClick={applyFilter}>
                      {t('vehicles.continue')}
                    </Button>
                  </div>
                </Popover.Content>
              </Popover>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='truck' />} disabled={disabled} data-testid='vehicles-multiselect'>
        {title || t('vehicles.title')} {countLabel}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectVehicles;
